import {html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {
	AttributeInterpreter,
	AttributeType,
	ChoiceValueDecisionState,
	ComponentDecisionState,
	ConfigurationInterpreter,
	DecisionKind
} from '@viamedici-spc/configurator-ts';

import {SpcBaseAttribute} from './spcBaseAttribute';
import {attributeIdToString} from './util/spcUtils';
import {staggeredReveal} from '../../css/styleSets';
import {attributeWidget} from '../../css/attributeWidget';
import info from '../../../icons/info';
import reset from '../../../icons/reset';
import down from '../../../icons/down';
import radio from '../../../icons/radio';
import radioChecked from '../../../icons/radio-checked';
import {selectFirstSection, selectProductData, selectSpcAttributes, selectSpcComponents, selectSpcConfiguration, selectSpcIsSatisfied} from '../../selectors';

@customElement('spc-component-attribute')
export class SpcComponentAttribute extends SpcBaseAttribute {

	static styles = [
		staggeredReveal,
		attributeWidget,
		css`
			:host {
				height: 100%;
				padding: 0;
			}

			@media (max-width: 768px) {
				:host {
					padding: 0 var(--small-padding);
				}
			}

			:host::-webkit-scrollbar {
				display: none;
			}

			.componentAttribute {
				border: 1px solid var(--color-grey4);
				margin-bottom: var(--small-padding);
			}
			.header {
				display: flex;
				gap: var(--small-padding);
				font: var(--font-widget-label);
				color: var(--color-grey4);
				padding: var(--very-small-padding)  var(--padding) var(--very-small-padding)  var(--padding);
				break-inside: avoid;
				break-after: avoid;
				position: relative;
				cursor: pointer;
				user-select: none;
			}

			.content {
				display: none;
				padding: 0 var(--padding) 0 var(--padding);
			}

			.componentAttribute.active .content {
				display: block;
			}

			.components {
				width: 100%;
				height: 100%;
				box-sizing: border-box;
			}

			.components.grid {
				display: grid;
				grid-auto-flow: row dense;
				gap: var(--features-column-gap, 48px);
				grid-template-columns: repeat(auto-fit, minmax(var(--features-column-width), 1fr));
			}
			.component {
				position: relative;
				break-inside: avoid;
			}



			.attributes {
				width: 100%;
				height: 100%;
				padding: var(--padding) 0 var(--large-padding) 0;
				box-sizing: border-box;
			}

			.attributes.grid {
				display: grid;
				grid-auto-flow: row dense;
				gap: var(--features-column-gap, 48px);
				grid-template-columns: repeat(auto-fit, minmax(var(--features-column-width), 1fr));
			}

			.attribute {
				position: relative;
				break-inside: avoid;
			}

			.icon.toggle {
				margin-left: auto;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform 0.25s ease-out;
			}
			.icon.toggle svg {
				display: block;
			}
			.active .icon.toggle {
				transform: rotate(180deg);
			}
		`];

	@property({type: Boolean})
	active = false;

	toggle() {
		this.active = !this.active;
	}

	stateChanged(state) {
		super.stateChanged(state);
		this.section = selectFirstSection(state);
		this.debugShowAll = state.status.debugShowAll;
		this.productData = selectProductData(state);

		this.spcIsSatisfied = selectSpcIsSatisfied(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.key = this.attribute.key;
		this.path = this.attribute.id.componentPath;
		this.local = this.attribute.id.localId;
		this.nextLevelPath = this.path === undefined ? [this.local] : [...this.path, this.local];

		this.spcComponents = selectSpcComponents(state)?.filter(component => (JSON.stringify(component.id.componentPath) === JSON.stringify(this.nextLevelPath)))
			.filter((component) => !(component.possibleDecisionStates.length === 1 && component.possibleDecisionStates[0] === 'Excluded'))
			.sort((a, b) => a.key.localeCompare(b.key));

		// console.log('SpcComponentAttribute', this.key, this.attribute, this.spcComponents, this.spcAttributes);

		this.spcAttributes = selectSpcAttributes(state)?.filter(attribute => (JSON.stringify(attribute.id.componentPath) === JSON.stringify(this.nextLevelPath)))
			.filter(spcAttribute => spcAttribute.type === 'Choice')
			.filter(spcAttribute => Array.from(spcAttribute.values.values()).some(v => !v.isPossibleDecisionStatesImmutable))
			.sort((a, b) => a.key.localeCompare(b.key));

		console.log('ComponentAttribute', this.key, this.attribute);

	}
	async resetDecision() {
		this.session.makeDecision({
			type: AttributeType.Component,
			attributeId: this.attribute.id,
			state: null
		});
	}
	isSatisfied() {
		return this.spcAttributes && this.spcAttributes.every(attribute => attribute.isSatisfied);
		//return this.attribute?.isSatisfied ?? false;
	}
	render() {
		if ((!this.spcComponents || this.spcComponents.length === 0) && (!this.spcAttributes || this.spcAttributes.length === 0)) return null;
		const zindex = 100;
		return html`
			<div class="componentAttribute ${classMap({satisfied: this.isSatisfied(), active: this.active})}">
				<div class="header" @click="${this.toggle}">
					<div class='icon indicator'>${this.isSatisfied() ? radioChecked : radio}</div>
					<span class="label">${this.attribute.key} (${this.attribute.inclusion})</span>
					<div class='icon toggle'>${down}</div>
				</div>
				<div class="content">
					${when(this.spcAttributes?.length > 0, () => html`
						<div class="attributes grid" id="attributesReference">
							${repeat(this.spcAttributes,
								(spcAttribute) => spcAttribute.key,
								// 'colorSelect','compactSelect', 'comboSelect', 'reducedSelect', 'reducedChoiceSelect'
								(spcAttribute, index) => html`
									<div class="attribute" style="z-index: ${zindex - (2 + index)}">
										<spc-attribute
											.attribute=${spcAttribute}
											.hideDisabledValues=${false}
											.displayStyle=${'simpleSelect'}
											.addon=${this.section.attributeOptions?.[spcAttribute.key]?.addon}>
										</spc-attribute>
									</div>
								`
							)}
						</div>
					`)}


					${when(this.spcComponents?.length > 0, () => html`
						<div class="components" id="componentsReference">
							${repeat(this.spcComponents,
								(component) => component.key,
								(component, index) => html`
									<div class="component">
										<spc-component-attribute .attribute=${component}></spc-component-attribute>
									</div>
								`
							)}
						</div>
					`)}
				</div>
			</div>

		`;
	}
}
